import React from 'react';
import { Button, useSnackbar } from '@sparknz/spark-pattern-library';
import {
  LinkRegion,
  trackCustomLink,
  useTriggerableTrackPageView,
} from '@sparknz/tracking';

import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import {
  ActionLayerStatus,
  PlanBundleOptions,
  WearableEligibilityCheckSteps,
} from 'journey/productDetails/typings';
import {
  getProductDetailsPageViewParams,
  trackLinkedProductsEvent,
} from 'journey/productDetails/utils';
import { getLineNumber } from 'journey/productDetails/utils/getLineNumber.utils';
import { useSegment } from 'shared/hooks';

interface WearableExistingPlanSelectorButtonProps {
  selectedLineNumber: string;
}

const WearableExistingPlanSelectorButton = ({
  selectedLineNumber,
}: WearableExistingPlanSelectorButtonProps) => {
  const {
    wearableEligibility: {
      eligibility: { eligibleLines, ineligibleLines },
    },
    dispatch,
    selectedDevice,
  } = useProductDetails();
  const { isBusiness } = useSegment();
  const { showSnackbar } = useSnackbar();
  const { trackPageView } = useTriggerableTrackPageView();

  const { primaryCategory, groupName } = selectedDevice;

  function handleContinueClick() {
    dispatch({
      type: ProductDetailsActionTypes.SetWearableEligibility,
      wearableEligibility: {
        activeStep: WearableEligibilityCheckSteps.AddToCart,
        selectedLineNumber: getLineNumber(selectedLineNumber),
      },
      planBundleOption: PlanBundleOptions.ChooseNewPlan,
      actionLayerStatus: ActionLayerStatus.Close,
    });

    trackCustomLink('Continue');
    trackPageView(
      getProductDetailsPageViewParams({
        groupName,
        primaryCategory,
      }),
    );

    trackLinkedProductsEvent({
      eventInfo: {
        eventName: 'Product View',
        eventAction: 'shop.prodViewAction',
      },
      device: selectedDevice,
      isBusiness,
    });

    showSnackbar({
      text: 'Great, this plan is eligible, click Add to cart to continue.',
    });
  }

  const hasOnlyOneEligibleLine = eligibleLines.length === 1;
  const lines = [
    ...eligibleLines.map((line) => ({ ...line, isEligible: true })),
    ...ineligibleLines.map((line) => ({ ...line, isEligible: false })),
  ];

  const isEligibleLine =
    lines.find(
      ({ lineNumber, offerId }) =>
        `${lineNumber}-${offerId}` === selectedLineNumber,
    )?.isEligible || false;

  return (
    <LinkRegion regionId="productDetails.line.complete">
      <Button
        variant="primary"
        fullWidth
        mb="s2"
        onClick={handleContinueClick}
        disabled={
          (!hasOnlyOneEligibleLine && !selectedLineNumber) || !isEligibleLine
        }
      >
        Continue
      </Button>
    </LinkRegion>
  );
};

export default WearableExistingPlanSelectorButton;
